import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, catchError, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export type AuthUser = { id: string; email: string; name?: string; role?: string };
export type AuthResponse = { accessToken: string; user: AuthUser };
export type RegisterRequest = { name?: string; email: string; password: string };
export type LoginRequest = { email: string; password: string };
export type AcceptInviteRequest = { token: string; name?: string; password: string };

const TOKEN_KEY = 'accesshub_web_token';
const USER_KEY = 'accesshub_web_user';
const INTENT_KEY = 'accesshub_web_intent';

@Injectable({ providedIn: 'root' })
export class WebAuthService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly userSubject = new BehaviorSubject<AuthUser | null>(null);
  readonly user$ = this.userSubject.asObservable();

  constructor() {
    this.hydrateUser();
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  getToken(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(TOKEN_KEY);
  }

  setIntent(intent: string): void {
    if (!this.isBrowser) return;
    localStorage.setItem(INTENT_KEY, intent);
  }

  getIntent(): string | null {
    if (!this.isBrowser) return null;
    return localStorage.getItem(INTENT_KEY);
  }

  clearIntent(): void {
    if (!this.isBrowser) return;
    localStorage.removeItem(INTENT_KEY);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && token.trim().length > 0;
  }

  getUserSnapshot(): AuthUser | null {
    return this.userSubject.value;
  }

  ensureUserLoaded(): Observable<AuthUser | null> {
    if (!this.isAuthenticated()) {
      this.userSubject.next(null);
      return of(null);
    }
    if (this.userSubject.value) return of(this.userSubject.value);
    return this.fetchCurrentUser();
  }

  login(dto: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiBaseUrl}/auth/login`, dto).pipe(
      tap((res) => {
        this.persistToken(res.accessToken);
        this.persistUser(res.user);
      }),
    );
  }

  register(dto: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiBaseUrl}/auth/register`, dto).pipe(
      tap((res) => {
        this.persistToken(res.accessToken);
        this.persistUser(res.user);
      }),
    );
  }

  acceptInvite(dto: AcceptInviteRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiBaseUrl}/invites/accept`, dto).pipe(
      tap((res) => {
        this.persistToken(res.accessToken);
        this.persistUser(res.user);
      }),
    );
  }

  fetchCurrentUser(): Observable<AuthUser | null> {
    if (!this.isAuthenticated()) {
      this.userSubject.next(null);
      return of(null);
    }

    return this.http.get<AuthUser>(`${environment.apiBaseUrl}/auth/me`).pipe(
      tap((user) => this.persistUser(user)),
      catchError(() => {
        this.userSubject.next(null);
        return of(null);
      }),
    );
  }

  logout(): void {
    if (!this.isBrowser) return;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.userSubject.next(null);
  }

  private persistToken(token: string): void {
    if (!this.isBrowser) return;
    if (!token || !token.trim().length) return;
    localStorage.setItem(TOKEN_KEY, token);
  }

  private persistUser(user?: AuthUser | null): void {
    if (!this.isBrowser) return;
    if (!user) {
      this.userSubject.next(null);
      localStorage.removeItem(USER_KEY);
      return;
    }
    this.userSubject.next(user);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  private hydrateUser(): void {
    if (!this.isBrowser) return;
    try {
      const raw = localStorage.getItem(USER_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as AuthUser;
      if (parsed?.id) {
        this.userSubject.next(parsed);
      }
    } catch {
      // ignore parse errors
    }
  }
}
